import React, { memo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import MenuItem from "./MenuItem";
import RowBadge, { resolveBadgeHint } from "./RowBadge";

const renderSectionIcon = (icon, isActive) => {
  if (!icon) return null;
  if (icon.lucide) return null; // top-level sections don't use lucide currently
  const src = isActive ? icon.active : icon.inactive;
  if (!src) return null;
  return <img src={src} alt="" className="sb-sidebar__row-icon-img" />;
};

const MenuSection = ({ section, ctx }) => {
  const {
    can,
    canAny,
    isMatch,
    isExpanded,
    toggleSection,
    counters,
    onSubItemNavigate,
  } = ctx;

  // Visibility
  if (section.anyPermission && !canAny(section.anyPermission)) return null;
  if (section.permission && !can(section.permission)) return null;

  const active = isMatch(section.matches);
  const badgeValue =
    section.badge && counters[section.badge] && counters[section.badge] > 0
      ? counters[section.badge]
      : null;

  // Single-link section (no expansion)
  if (section.type === "link") {
    return (
      <Link
        to={section.to || "#"}
        className={
          active
            ? "sb-sidebar__row sb-sidebar__row--top sb-sidebar__row--active"
            : "sb-sidebar__row sb-sidebar__row--top"
        }
        onClick={() => onSubItemNavigate({})}
      >
        <span className="sb-sidebar__row-icon">
          {renderSectionIcon(section.icon, active)}
        </span>
        <span className="sb-sidebar__row-label" title={section.label}>{section.label}</span>
        {badgeValue !== null && (
          <RowBadge
            value={badgeValue}
            hint={resolveBadgeHint(section, badgeValue, counters)}
          />
        )}
      </Link>
    );
  }

  // Filter visible items so an empty section folds away
  const visibleItems = (section.items || []).filter((it) => {
    if (it.permission) return can(it.permission);
    if (it.anyPermission) return canAny(it.anyPermission);
    return true;
  });

  if (visibleItems.length === 0) return null;

  const expanded = isExpanded(section.key);

  return (
    <div
      className={
        active
          ? "sb-sidebar__group sb-sidebar__group--top sb-sidebar__group--active"
          : "sb-sidebar__group sb-sidebar__group--top"
      }
    >
      <button
        type="button"
        className={
          active
            ? "sb-sidebar__row sb-sidebar__row--top sb-sidebar__row--header sb-sidebar__row--active"
            : "sb-sidebar__row sb-sidebar__row--top sb-sidebar__row--header"
        }
        onClick={() => toggleSection(section.key)}
        aria-expanded={expanded}
      >
        <span className="sb-sidebar__row-icon">
          {renderSectionIcon(section.icon, active)}
        </span>
        <span className="sb-sidebar__row-label" title={section.label}>{section.label}</span>
        {badgeValue !== null && (
          <RowBadge
            value={badgeValue}
            hint={resolveBadgeHint(section, badgeValue, counters)}
          />
        )}
        <ChevronDown
          size={14}
          className={
            expanded
              ? "sb-sidebar__row-chevron sb-sidebar__row-chevron--open"
              : "sb-sidebar__row-chevron"
          }
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="sb-sidebar__group-body">
          {visibleItems.map((item) => (
            <MenuItem
              key={item.key || `${section.key}-${item.to || item.label}`}
              item={item}
              ctx={ctx}
              depth={1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(MenuSection);
